import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import type { ChatMessage, PublicUser } from '@snakefeast/shared';
import { Icon } from '../components/Icon';
import { Avatar, Confirm, Flag, Modal, PresenceDot, Spinner } from '../components/ui';
import { api, ApiError } from '../lib/api';
import { lobbySocket } from '../lib/lobbySocket';
import { useLobby } from '../store/lobby';
import { useSession } from '../store/session';
import { toast } from '../store/toasts';
import { watchUser } from './Live';
import { useChatAllowed } from '../lib/portal';
import { GameIcon, Glyph } from '../components/GameIcon';

type Friend = PublicUser & { status: 'online' | 'offline' | 'in_game' };
interface FriendList {
  friends: Friend[];
  incoming: PublicUser[];
  outgoing: PublicUser[];
}

export let activeChatUserId: string | null = null;

function Chat({ friend, onClose }: { friend: Friend; onClose: () => void }) {
  const { t } = useTranslation();
  // The portal may switch chat off.
  const chatOn = useChatAllowed();
  const me = useSession((s) => s.user)!;
  const live = useLobby((s) => s.chat[friend.id]) ?? [];
  const markRead = useLobby((s) => s.markRead);
  const [history, setHistory] = useState<ChatMessage[] | null>(null);
  const [text, setText] = useState('');
  const [hasMore, setHasMore] = useState(true);
  const logRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    activeChatUserId = friend.id;
    markRead(friend.id);
    void api<ChatMessage[]>(`/api/messages/${friend.id}`).then((h) => {
      setHistory(h);
      setHasMore(h.length >= 40);
    });
    void api(`/api/messages/${friend.id}/read`, { body: {} }).catch(() => {});
    return () => {
      activeChatUserId = null;
    };
  }, [friend.id, markRead]);

  const all = [...(history ?? []), ...live.filter((m) => !history?.some((h) => h.id === m.id))];
  useEffect(() => {
    logRef.current?.scrollTo({ top: logRef.current.scrollHeight });
  }, [all.length]);

  const loadMore = async () => {
    const first = history?.[0];
    if (!first) return;
    const older = await api<ChatMessage[]>(`/api/messages/${friend.id}?before=${first.id}`);
    setHasMore(older.length >= 40);
    setHistory([...older, ...(history ?? [])]);
  };

  const send = async (e: React.FormEvent) => {
    e.preventDefault();
    const body = text.trim();
    if (!body) return;
    const clientId = crypto.randomUUID();
    setText('');
    // Optimistic bubble; replaced when the server echo with the same clientId arrives.
    const optimistic: ChatMessage = { id: `tmp-${clientId}`, fromUserId: me.id, toUserId: friend.id, body, createdAt: new Date().toISOString(), clientId };
    const s = useLobby.getState();
    useLobby.setState({ chat: { ...s.chat, [friend.id]: [...(s.chat[friend.id] ?? []), optimistic] } });
    try {
      await api(`/api/messages/${friend.id}`, { body: { body, clientId } });
    } catch (err) {
      const s2 = useLobby.getState();
      useLobby.setState({ chat: { ...s2.chat, [friend.id]: (s2.chat[friend.id] ?? []).filter((m) => m.clientId !== clientId) } });
      toast(err instanceof ApiError ? t(`errors.${err.code}`) : t('errors.network'), 'error');
      setText(body);
    }
  };

  return (
    <Modal title={friend.displayName} onClose={onClose}>
      <div className="chat">
        <div className="chat-log" ref={logRef} aria-live="polite">
          {history === null ? (
            <div className="empty"><Spinner /></div>
          ) : (
            <>
              {hasMore && history.length > 0 && <button className="btn small ghost" onClick={loadMore}>{t('chat.loadMore')}</button>}
              {all.length === 0 && <div className="empty">{t('chat.empty')}</div>}
              {all.map((m) => (
                <div key={m.id} className={`bubble ${m.fromUserId === me.id ? 'mine' : 'theirs'}${m.id.startsWith('tmp-') ? ' pending' : ''}`}>
                  {m.body}
                </div>
              ))}
            </>
          )}
        </div>
        {chatOn ? (
          <form className="chat-input" onSubmit={send}>
            <input className="input" maxLength={500} placeholder={t('chat.placeholder')} value={text} onChange={(e) => setText(e.target.value)} aria-label={t('chat.placeholder')} />
            <button className="btn primary icon" aria-label={t('common.send')} disabled={!text.trim()}>
              <Icon name="send" size={18} />
            </button>
          </form>
        ) : (
          <div className="chat-input faint">{t('chat.disabled')}</div>
        )}
      </div>
    </Modal>
  );
}

export function Friends() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const me = useSession((s) => s.user)!;
  const presence = useLobby((s) => s.presence);
  const unread = useLobby((s) => s.unread);
  const version = useLobby((s) => s.friendsVersion);
  const outgoing = useLobby((s) => s.outgoingChallenge);
  const [list, setList] = useState<FriendList | null>(null);
  const [q, setQ] = useState('');
  const [results, setResults] = useState<PublicUser[]>([]);
  const [chatWith, setChatWith] = useState<Friend | null>(null);
  const [confirm, setConfirm] = useState<null | { text: string; run: () => Promise<void> }>(null);

  const load = useCallback(() => api<FriendList>('/api/friends').then(setList).catch(() => {}), []);
  useEffect(() => {
    if (!me.isGuest) void load();
  }, [load, version, me.isGuest]);

  useEffect(() => {
    if (q.trim().length < 2) {
      setResults([]);
      return;
    }
    const id = setTimeout(() => {
      void api<PublicUser[]>(`/api/users/search?q=${encodeURIComponent(q.trim())}`).then(setResults).catch(() => {});
    }, 250);
    return () => clearTimeout(id);
  }, [q]);

  if (me.isGuest) {
    return (
      <div className="page">
        <div className="page-header"><h1>{t('friends.title')}</h1></div>
        <div className="card empty"><GameIcon name="lock" size={40} />{t('friends.guestsLocked')}</div>
      </div>
    );
  }

  const request = async (userId: string) => {
    try {
      const r = await api<{ status: string }>('/api/friends/requests', { body: { userId } });
      toast(r.status === 'accepted' ? t('friends.acceptedBy', { name: '' }) : t('friends.requestSent'), 'success');
      setQ('');
      void load();
    } catch (e) {
      toast(e instanceof ApiError ? e.message : t('errors.network'), 'error');
    }
  };
  const act = async (fn: () => Promise<unknown>) => {
    try {
      await fn();
      void load();
    } catch (e) {
      toast(e instanceof ApiError ? e.message : t('errors.network'), 'error');
    }
  };
  const challenge = (f: Friend) => {
    lobbySocket.send({ t: 'challenge.send', toUserId: f.id, mode: 'duel' });
  };

  const status = (f: Friend) => presence[f.id] ?? f.status;

  return (
    <div className="page">
      <div className="page-header"><h1>{t('friends.title')}</h1></div>

      <div className="card">
        <div className="row">
          <Icon name="search" />
          <input className="input" placeholder={t('friends.addPlaceholder')} value={q} onChange={(e) => setQ(e.target.value)} aria-label={t('friends.add')} />
        </div>
        {results.length > 0 && (
          <div className="list mt">
            {results.map((u) => (
              <div key={u.id} className="list-item">
                <Avatar avatar={u.avatar} avatarUrl={u.avatarUrl} name={u.displayName} size={36} premium={u.isPremium} />
                <Link to={`/u/${u.id}`} className="grow truncate" style={{ color: 'inherit' }}>
                  <strong>{u.displayName}</strong> <span className="faint">@{u.username}</span>
                </Link>
                <button className="btn small primary" onClick={() => request(u.id)}>
                  <Icon name="plus" size={16} /> {t('friends.add')}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {list && list.incoming.length > 0 && (
        <div className="card mt">
          <h2>{t('friends.requests')}</h2>
          {list.incoming.map((u) => (
            <div key={u.id} className="list-item">
              <Avatar avatar={u.avatar} avatarUrl={u.avatarUrl} name={u.displayName} size={36} />
              <span className="grow truncate"><strong>{u.displayName}</strong></span>
              <button className="btn small ghost" onClick={() => act(() => api(`/api/friends/${u.id}`, { method: 'DELETE' }))}>{t('friends.decline')}</button>
              <button className="btn small primary" onClick={() => act(() => api(`/api/friends/${u.id}/accept`, { body: {} }))}>{t('friends.accept')}</button>
            </div>
          ))}
        </div>
      )}

      <div className="card mt">
        {!list ? (
          <div className="stack">{Array.from({ length: 4 }, (_, i) => <div key={i} className="skeleton" style={{ height: 52 }} />)}</div>
        ) : list.friends.length === 0 ? (
          <div className="empty">{t('friends.none')}<div className="mt faint">@{me.username}</div></div>
        ) : (
          <div className="list">
            {list.friends.map((f) => (
              <div key={f.id} className="list-item">
                <div style={{ position: 'relative' }}>
                  <Avatar avatar={f.avatar} avatarUrl={f.avatarUrl} name={f.displayName} size={42} premium={f.isPremium} />
                  <span style={{ position: 'absolute', bottom: 0, right: 0 }}><PresenceDot status={status(f)} /></span>
                </div>
                <Link to={`/u/${f.id}`} className="grow truncate" style={{ color: 'inherit' }}>
                  <strong>{f.displayName}</strong> <Flag country={f.country} />
                  <div className="faint">{status(f) === 'online' ? t('common.online') : status(f) === 'in_game' ? t('common.inGame') : t('common.offline')} · {f.rating}</div>
                </Link>
                <button className="btn small icon ghost" style={{ position: 'relative' }} onClick={() => setChatWith(f)} aria-label={t('friends.message')}>
                  <Icon name="chat" size={18} />
                  {(unread[f.id] ?? 0) > 0 && <span className="badge" style={{ position: 'absolute', top: -4, right: -4 }}>{unread[f.id]}</span>}
                </button>
                {status(f) === 'in_game' ? (
                  <button className="btn small" onClick={() => void watchUser(f.id, nav, t)} title={t('live.watch')}>
                    <Glyph e="👁" /> <span className="hide-sm">{t('live.watch')}</span>
                  </button>
                ) : (
                  <button className="btn small" disabled={status(f) !== 'online' || !!outgoing} onClick={() => challenge(f)} title={t('friends.challenge')}>
                    <Icon name="swords" size={16} /> <span className="hide-sm">{t('friends.challenge')}</span>
                  </button>
                )}
                <button className="btn small icon ghost" aria-label={t('friends.remove')} onClick={() => setConfirm({ text: t('friends.removeConfirm', { name: f.displayName }), run: () => act(() => api(`/api/friends/${f.id}`, { method: 'DELETE' })) })}>
                  <Icon name="x" size={16} />
                </button>
              </div>
            ))}
          </div>
        )}
        {list && list.outgoing.length > 0 && (
          <div className="mt">
            <div className="faint">{t('friends.outgoing')}</div>
            {list.outgoing.map((u) => (
              <div key={u.id} className="list-item">
                <Avatar avatar={u.avatar} avatarUrl={u.avatarUrl} name={u.displayName} size={30} />
                <span className="grow truncate muted">{u.displayName}</span>
                <button className="btn small ghost" onClick={() => act(() => api(`/api/friends/${u.id}`, { method: 'DELETE' }))}>{t('common.cancel')}</button>
              </div>
            ))}
          </div>
        )}
      </div>

      {chatWith && <Chat friend={chatWith} onClose={() => setChatWith(null)} />}
      {confirm && (
        <Confirm text={confirm.text} danger onCancel={() => setConfirm(null)} onConfirm={() => { void confirm.run(); setConfirm(null); }} />
      )}
    </div>
  );
}
